import { createEventHook } from '@vueuse/shared'
import { ref } from 'vue'
import type { Ref } from 'vue'

import { useAuthStore } from '@/store/auth.js'

import createSocialAuthWindow from '@/utils/social.js'

/**
 * In the future, you can add new providers here
 */
type SocialProvider = 'esia'

/**
 * Parameter determining the operation: linking ('link') or login ('login')
 */
type SocialAction = 'link' | 'login'

/**
 * Interface for the return type of social auth functions
 */
interface SocialAuthReturn {
  readonly isPending: Ref<boolean>
  onSuccess: (fn: (data: any) => void) => { off: () => void }
  onError: (fn: (error: any) => void) => { off: () => void }
  open: () => void
}

/**
 * Universal social authentication hook with shared state
 * @param type The social action type ('link' or 'login')
 * @returns Object with shared event handlers and an open function that accepts a provider
 */
export function useSocialAuth(type: SocialAction): {
  readonly isPending: Ref<boolean>
  onSuccess: (fn: (data: any) => void) => { off: () => void }
  onError: (fn: (error: any) => void) => { off: () => void }
  open: (social: SocialProvider) => void
} {
  const isPending = ref(false)
  // Create shared event hooks
  const { on: onSuccess, trigger: triggerSuccess } = createEventHook<any>()
  const { on: onError, trigger: triggerError } = createEventHook<any>()

  // Access the authentication store
  const auth = useAuthStore()

  // Get the appropriate auth method based on action type
  const authMethod = type === 'link' ? auth.linkSocial : auth.loginViaSocial

  // Create a universal listener factory for the auth window
  const createListener = (social) => {
    return async (event: MessageEvent<{ event: string; query: unknown }>) => {
      if (event && event.data.event === 'auth') {
        isPending.value = true
        await authMethod(event.data.query)
          .then((response) => triggerSuccess({ social, event, response }))
          .catch((error) => triggerError({ social, event, error }))
          .finally(() => (isPending.value = false))
      }
    }
  }

  // Function to open the auth window with the specified provider
  const open = (social: SocialProvider) => {
    createSocialAuthWindow(social, createListener(social))
  }

  return { isPending, onSuccess, onError, open }
}
